import React from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { useCart } from "@/context/CartContext";
import { useLang } from "@/context/LanguageContext";
import { formatRON, imgUrl } from "@/lib/api";

const CartDrawer = () => {
  const { items, open, setOpen, removeItem, updateQty, subtotal, count } = useCart();
  const { t, lang } = useLang();
  const navigate = useNavigate();

  const goCheckout = () => {
    setOpen(false);
    navigate("/checkout");
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            onClick={() => setOpen(false)}
            className="fixed inset-0 z-50 bg-foreground/40 backdrop-blur-sm"
            data-testid="cart-overlay"
          />
          <motion.aside
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
            className="fixed right-0 top-0 z-50 flex h-full w-full max-w-md flex-col border-l border-border bg-background"
            data-testid="cart-drawer"
          >
            <div className="flex items-center justify-between border-b border-border p-5">
              <h2 className="text-xl font-black uppercase tracking-tight">
                {t.cart.title} [{count}]
              </h2>
              <button onClick={() => setOpen(false)} data-testid="cart-close" className="font-mono text-xs uppercase tracking-widest hover:text-accent">
                ✕ CLOSE
              </button>
            </div>

            <div className="flex-1 overflow-y-auto no-scrollbar">
              {items.length === 0 ? (
                <div className="flex h-full flex-col items-center justify-center gap-4 p-8">
                  <p className="font-mono text-xs uppercase tracking-widest text-muted-foreground">{t.cart.empty}</p>
                  <button
                    onClick={() => { setOpen(false); navigate("/shop"); }}
                    className="border border-border px-6 py-3 font-mono text-xs uppercase tracking-widest transition-colors hover:bg-foreground hover:text-background"
                  >
                    {t.cart.continue}
                  </button>
                </div>
              ) : (
                items.map((item) => {
                  const key = `${item.slug}-${item.size}-${item.color}`;
                  return (
                    <div key={key} className="flex gap-4 border-b border-border p-4" data-testid={`cart-item-${item.slug}`}>
                      <img src={imgUrl(item.image)} alt={item.name} className="h-28 w-24 flex-shrink-0 object-cover" />
                      <div className="flex flex-1 flex-col justify-between">
                        <div>
                          <div className="flex justify-between gap-2">
                            <h3 className="font-bold uppercase tracking-tight leading-tight">{item.name}</h3>
                            <span className="font-mono text-sm">{formatRON(item.price)}</span>
                          </div>
                          <p className="mt-1 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                            {t.cart.size}: {item.size} · {t.cart.color}: {item.color}
                          </p>
                        </div>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center border border-border">
                            <button onClick={() => updateQty(key, item.qty - 1)} className="px-3 py-1 font-mono hover:bg-muted" data-testid={`cart-dec-${item.slug}`}>−</button>
                            <span className="px-3 font-mono text-sm">{item.qty}</span>
                            <button onClick={() => updateQty(key, item.qty + 1)} className="px-3 py-1 font-mono hover:bg-muted" data-testid={`cart-inc-${item.slug}`}>+</button>
                          </div>
                          <button onClick={() => removeItem(key)} className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground hover:text-accent" data-testid={`cart-remove-${item.slug}`}>
                            {t.cart.remove}
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            {items.length > 0 && (
              <div className="border-t border-border p-5">
                <div className="flex justify-between font-mono text-xs uppercase tracking-widest text-muted-foreground">
                  <span>{t.cart.shipping}</span><span>{lang === "ro" ? "la checkout" : "at checkout"}</span>
                </div>
                <div className="mt-2 flex items-end justify-between">
                  <span className="font-mono text-xs uppercase tracking-widest">{t.cart.total}</span>
                  <span className="text-2xl font-black">{formatRON(subtotal)}</span>
                </div>
                <button
                  onClick={goCheckout}
                  data-testid="cart-checkout"
                  className="mt-4 w-full bg-foreground py-4 font-mono text-sm uppercase tracking-widest text-background transition-colors hover:bg-accent"
                >
                  {t.cart.checkout}
                </button>
              </div>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
};

export default CartDrawer;
